import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { CashJournal, CashJournalService, CashHistoryStats } from '../service/cash-journal.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-cash-history',
    templateUrl: './cash-history.component.html',
    styleUrls: ['./cash-history.component.css']
})
export class CashHistoryComponent implements OnInit, OnDestroy {

    journals: CashJournal[] = [];
    displayedJournals: CashJournal[] = [];

    // Pagination State
    currentPage: number = 0;
    pageSize: number = 20;
    totalPages: number = 0;
    totalElements: number = 0;

    // Filters
    filterType: string = '';
    filterOrigin: string = '';
    filterUser: string = '';
    startDate: string = '';
    endDate: string = '';

    // Statistics
    stats: CashHistoryStats = {
        total: 0,
        today: 0,
        withOrder: 0,
        withoutOrder: 0,
        shortcuts: 0,
        virtual: 0
    };

    // Edit/Delete state
    editingF8: CashJournal | null = null;
    editAmount: number | null = null;
    editReason: string = '';
    editComment: string = '';

    showDeleteConfirm: boolean = false;
    f8ToDeleteId: number | null = null;

    isLoading: boolean = false;

    constructor(
        private cashJournalService: CashJournalService,
        private renderer: Renderer2,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.renderer.addClass(document.body, 'sneat-bg');
        this.loadHistory();
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(document.body, 'sneat-bg');
    }

    loadHistory() {
        this.isLoading = true;
        const filters = {
            type: this.filterType,
            origin: this.filterOrigin,
            username: this.filterUser,
            startDate: this.startDate,
            endDate: this.endDate
        };

        this.cashJournalService.getHistoryPage(this.currentPage, this.pageSize, filters).subscribe({
            next: (data) => {
                this.displayedJournals = data.content;
                this.totalPages = data.totalPages;
                this.totalElements = data.totalElements;
                this.isLoading = false;
            },
            error: (e) => {
                console.error('Error loading history page:', e);
                this.isLoading = false;
            }
        });

        this.cashJournalService.getHistoryStats().subscribe({
            next: (statsData) => {
                this.stats = statsData;
            },
            error: (e) => console.error('Error loading stats:', e)
        });
    }

    goToOrderDetails(orderId: number) {
        if (orderId) {
            this.router.navigate(['/orderDetails'], { queryParams: { id: orderId } });
        }
    }

    getOriginLabel(origin: string): string {
        switch (origin) {
            case 'BUTTON': return 'Bouton Manuel';
            case 'SHORTCUT': return 'Raccourci F8';
            case 'ORDER_CREATION': return 'Création Commande';
            case 'ORDER_VALIDATION': return 'Validation Commande';
            default: return origin;
        }
    }

    getOriginIcon(origin: string): string {
        switch (origin) {
            case 'BUTTON': return 'fa-mouse-pointer';
            case 'SHORTCUT': return 'fa-keyboard';
            case 'ORDER_CREATION': return 'fa-plus-circle';
            case 'ORDER_VALIDATION': return 'fa-check-circle';
            default: return 'fa-info-circle';
        }
    }

    onFilterChange() {
        this.currentPage = 0; // Reset to first page when filtering
        this.loadHistory();
    }

    resetFilters() {
        this.filterType = '';
        this.filterOrigin = '';
        this.filterUser = '';
        this.startDate = '';
        this.endDate = '';
        this.currentPage = 0;
        this.loadHistory();
    }

    // ─── PAGINATION ───────────────────────────────────────────────────────

    nextPage() {
        if (this.currentPage < this.totalPages - 1) {
            this.currentPage++;
            this.loadHistory();
        }
    }

    prevPage() {
        if (this.currentPage > 0) {
            this.currentPage--;
            this.loadHistory();
        }
    }

    // ─── EDIT F8 ──────────────────────────────────────────────────────────

    isF8Entry(j: CashJournal): boolean {
        return j.origin === 'SHORTCUT' && j.amount != null && j.amount > 0;
    }

    openEditDialog(f8: CashJournal) {
        this.editingF8 = { ...f8 };
        this.editAmount = f8.amount ?? null;
        this.editReason = f8.reason ?? '';
        this.editComment = f8.comment ?? '';
        this.showDeleteConfirm = false;
    }

    closeEditDialog() {
        this.editingF8 = null;
        this.editAmount = null;
        this.editReason = '';
        this.editComment = '';
    }

    saveEditF8() {
        if (!this.editingF8?.id) return;
        this.isLoading = true;

        const updates: Partial<CashJournal> = {
            amount: this.editAmount ?? undefined,
            reason: this.editReason || undefined,
            comment: this.editComment || undefined
        };

        this.cashJournalService.updateF8(this.editingF8.id, updates).subscribe({
            next: () => {
                this.isLoading = false;
                this.closeEditDialog();
                this.loadHistory();
            },
            error: (e) => {
                this.isLoading = false;
                console.error('Erreur modification F8 :', e);
                alert('❌ Erreur lors de la modification');
            }
        });
    }

    // ─── DELETE F8 ────────────────────────────────────────────────────────

    askDeleteConfirm(id: number) {
        this.f8ToDeleteId = id;
        this.showDeleteConfirm = true;
        this.editingF8 = null;
    }

    cancelDelete() {
        this.showDeleteConfirm = false;
        this.f8ToDeleteId = null;
    }

    confirmDelete() {
        if (!this.f8ToDeleteId) return;
        this.isLoading = true;

        this.cashJournalService.deleteF8(this.f8ToDeleteId).subscribe({
            next: () => {
                this.isLoading = false;
                this.showDeleteConfirm = false;
                this.f8ToDeleteId = null;
                this.loadHistory();
            },
            error: (e) => {
                this.isLoading = false;
                console.error('Erreur suppression F8 :', e);
                alert('❌ Erreur lors de la suppression');
            }
        });
    }

    isBeingDeleted(id: number): boolean {
        return this.showDeleteConfirm && this.f8ToDeleteId === id;
    }
}
